import { branding } from '@/config/branding'
import { cn } from '@/lib/utils'
import { HelpCircle, type LucideIcon } from 'lucide-react'
import type React from 'react'
import { StateIcon, type StateTone } from './state-icon'

interface ErrorStateProps {
	title: string
	message?: string | React.ReactNode
	showSupportEmail?: boolean
	className?: string
	containerClassName?: string
	icon?: LucideIcon
	/** Matches the state panel family: an error is a warning unless a caller says otherwise. */
	tone?: StateTone
	actions?: React.ReactNode
	dataTestId?: string
}

export function ErrorState({
	title,
	message,
	showSupportEmail = true,
	className = '',
	containerClassName,
	icon: Icon = HelpCircle,
	tone = 'warning',
	actions,
	dataTestId,
}: ErrorStateProps) {
	const defaultMessage = showSupportEmail ? (
		<>
			Please contact{' '}
			<a
				href={`mailto:${branding.supportEmail}`}
				className='text-primary hover:underline'
				data-testid='error-state-support-link'>
				{branding.supportEmail}
			</a>{' '}
			for assistance
		</>
	) : (
		'Something went wrong'
	)

	return (
		<div className={cn('flex items-center justify-center w-full h-full', containerClassName)} data-testid={dataTestId}>
			{/* Same rhythm as the empty and access restricted panels: 16px between groups, 6px inside one. */}
			<div className={cn('flex max-w-sm flex-col items-center gap-4 px-6 py-8 text-center', className)}>
				<StateIcon icon={Icon} tone={tone} />
				<div className='flex flex-col gap-2'>
					<p className='text-state-title leading-tight font-semibold text-foreground text-balance'>{title}</p>
					<p className='text-company-name text-muted-foreground/90 leading-relaxed text-pretty'>
						{message ?? defaultMessage}
					</p>
				</div>
				{actions && <div className='flex gap-2 justify-center'>{actions}</div>}
			</div>
		</div>
	)
}
